/**
 * Local sales tax: the jurisdictions below the state, the rows a document
 * carries one per jurisdiction, and the matching that picks them off an
 * address (piece 25; decisions 0736, 0737, 0738).
 *
 * The product owner, 2026-09-25: *"there is a lookup table and the lookup table
 * is like state, county, municipality and if there is no search result in that
 * category like pa, alleghy returns 1% and Pa returns 6% then we show 2 tax
 * columns on the document Pa Tax XXX then Alleghy County Tax XXXX"*. The tax
 * shows as rows, never as columns on each line (the owner's third answer).
 *
 * 🔨 The State level stays the `SalesTaxRate` series. A local jurisdiction is a
 * `LocalTaxJurisdiction` with its own `LocalTaxRate` series, read through the
 * same `rowInForce` every other series reads through.
 */
import { US_STATE_NAMES } from '../records/taxExemption';
import { samePlaceName } from '../records/mailingAddress';
import { normaliseText } from '../records/normalise';
import { roundHalfUpCents } from '../money';
import { taxShare } from '../documents/invoice';
import { t } from '../strings';
import { nextRowAfter, rowInForce, type EffectiveDatedRow } from './series';

/** Every level a document's tax row names; the first is the state's own series. */
export const TAX_JURISDICTION_LEVELS = ['State', 'County', 'Municipality', 'SpecialDistrict'] as const;
export type TaxJurisdictionLevelValue = (typeof TAX_JURISDICTION_LEVELS)[number];

/** The levels a `LocalTaxJurisdiction` takes; State is never one, because the state is the rate table. */
export const LOCAL_TAX_LEVELS = ['County', 'Municipality', 'SpecialDistrict'] as const;
export type LocalTaxLevelValue = (typeof LOCAL_TAX_LEVELS)[number];

/**
 * 🔒 How far a state's local jurisdictions are modelled, on its rate row
 * (decision 0736). None: the state only, and no report. Complete: the matches
 * are the whole answer, so no match is no local tax. Partial: what matched is
 * charged and the ship-to is reported until a person confirms it.
 */
export const LOCAL_TAX_COVERAGES = ['None', 'Partial', 'Complete'] as const;
export type LocalTaxCoverageValue = (typeof LOCAL_TAX_COVERAGES)[number];

export function localTaxCoverageProblem(value: unknown): string | null {
  return (LOCAL_TAX_COVERAGES as readonly unknown[]).includes(value) ? null : `local coverage is one of ${LOCAL_TAX_COVERAGES.join(', ')}`;
}

/** One local jurisdiction an address matched, with the rate its series holds in force on the date. */
export interface LocalRateAnswer {
  readonly jurisdictionId: string;
  readonly level: LocalTaxLevelValue;
  readonly name: string;
  readonly ratePercent: number;
}

/**
 * 🔒 One jurisdiction's share of a document's tax (decision 0738): the label as
 * it reads on the document, the rate, the base it was charged on and what it
 * came to. A document stores these as `DocumentTaxRow` rows beside its lines.
 */
export interface TaxJurisdictionTotal {
  readonly level: TaxJurisdictionLevelValue;
  /** The `LocalTaxJurisdiction` the row came from; null for the state's own row. */
  readonly jurisdictionId: string | null;
  readonly stateCode: string;
  /** Snapshotted at the save, so a renamed jurisdiction never renames an issued invoice's row. */
  readonly label: string;
  readonly ratePercent: number;
  readonly taxableBaseCents: number;
  readonly taxCents: number;
}

/**
 * 🔒 One line's share of one of its document's rows (decision 0738): the row,
 * named by its level, its jurisdiction and the rate it charged, and what that
 * jurisdiction charged on the line. An Invoice and a Credit Note store each as
 * a `DocumentTaxShare`, so a credit gives back what each line was charged, in
 * the rows it was charged in, and re-derives nothing.
 */
export interface TaxLineShare {
  readonly level: TaxJurisdictionLevelValue;
  readonly jurisdictionId: string | null;
  readonly ratePercent: number;
  readonly taxCents: number;
}

/**
 * The key a document's row is summed under: its local jurisdiction, else the
 * state at the rate it charged, so a state charging a lower rate on some lines
 * holds a row per rate (decision 0901). One document names one state, so the
 * state's code is not part of it. The rate is read at the four places its
 * column keeps, so a row read back from the database keys as it was written.
 */
export function taxRowKey(row: { readonly level: TaxJurisdictionLevelValue; readonly jurisdictionId: string | null; readonly ratePercent: number }): string {
  return row.jurisdictionId ?? `${row.level}:${Math.round(row.ratePercent * 10_000) / 10_000}`;
}

/** A row's label: *Pennsylvania sales tax*, *Allegheny County sales tax*. */
export function jurisdictionTaxLabel(level: TaxJurisdictionLevelValue, stateCode: string, name: string | null): string {
  const named = level === 'State' ? US_STATE_NAMES[stateCode.toUpperCase()] ?? stateCode.toUpperCase() : (name ?? '').trim();
  return `${named} ${t('tax.preview.salesTax')}`;
}

/**
 * 🔒 A reduced-rate row's label (decision 1001): the jurisdiction, then the
 * category or categories charged at a Custom rate below the state's (decision
 * 0904), as in *Pennsylvania sales tax · Clothing, reduced rate*. The
 * jurisdiction's own row keeps its plain label, so a reduced rate is never
 * folded into it and never unnamed beside it.
 */
export function reducedRateTaxLabel(level: TaxJurisdictionLevelValue, stateCode: string, name: string | null, categories: readonly string[]): string {
  const base = jurisdictionTaxLabel(level, stateCode, name);
  const named = [...new Set(categories.map((c) => c.trim()).filter((c) => c.length > 0))];
  return named.length === 0 ? `${base} · ${t('tax.preview.reducedRate')}` : `${base} · ${named.join(', ')}, ${t('tax.preview.reducedRate')}`;
}

/** Rates add in percent without the float drift 6.25 plus 2 would otherwise carry into a stored column. */
export function addRatePercents(rates: readonly number[]): number {
  return Math.round(rates.reduce((sum, rate) => sum + rate, 0) * 10_000) / 10_000;
}

/**
 * 🔒 The rounding rule (decision 0736): each line's tax in each jurisdiction is
 * rounded once, half-up, the rule every line has always kept. A line's tax is
 * the sum of its jurisdictions' shares, and a jurisdiction's row is the sum of
 * its shares over the lines, so the rows, the lines and the document's total
 * are one set of figures added two ways.
 */
export function jurisdictionShareCents(amountCents: number, ratePercent: number): number {
  return roundHalfUpCents((amountCents * ratePercent) / 100);
}

const LEVEL_ORDER: Readonly<Record<TaxJurisdictionLevelValue, number>> = { State: 0, County: 1, Municipality: 2, SpecialDistrict: 3 };

/**
 * The order a document draws its rows in: the state first, then county,
 * municipality and district, each by name. A state charging a lower rate on
 * some lines carries two rows of one name, the higher rate first (decision 0901).
 */
export function sortJurisdictionTotals<T extends Pick<TaxJurisdictionTotal, 'level' | 'label'> & { ratePercent?: number }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level] || a.label.localeCompare(b.label) || (b.ratePercent ?? 0) - (a.ratePercent ?? 0));
}

// ---- a jurisdiction's line on the Sales tax page (decision 1020) ----

/** One local jurisdiction's series, split the way its line on the Sales tax page draws it. */
export interface LocalRateLine<T> {
  /** The row in force on the day; null where the series has none that day, as before a first row starts. */
  readonly inForce: T | null;
  /** The row the series moves to next after the day; null where nothing is scheduled. */
  readonly next: T | null;
  /** Every other row, newest first, which the line folds behind its chevron. */
  readonly earlier: T[];
}

const startOf = (row: EffectiveDatedRow): number => {
  const value = row.effectiveFrom;
  return (typeof value === 'string' ? new Date(value.length === 10 ? `${value}T00:00:00Z` : value) : value).getTime();
};

/**
 * 🔒 A local jurisdiction's line (decision 1020): the rate in force today, the
 * next scheduled rate, and the rest folded, newest first. Read through the same
 * `rowInForce` and `nextRowAfter` the calculation and the site's Tax
 * Jurisdiction read, so the three never disagree about which row stands.
 */
export function localRateLine<T extends EffectiveDatedRow>(rows: readonly T[], asOf: Date): LocalRateLine<T> {
  const inForce = rowInForce(rows, asOf);
  const next = nextRowAfter(rows, asOf);
  const earlier = rows.filter((row) => row !== inForce && row !== next).sort((a, b) => startOf(b) - startOf(a));
  return { inForce, next, earlier };
}

// ---- matching an address (decision 0737) ----

/** The suffixes a typed county may carry or leave off: *Allegheny* and *Allegheny County* are one county. */
export const COUNTY_NAME_SUFFIXES = ['County', 'Parish', 'Borough'] as const;

/** A county name folded through the one routine every name is (piece 10): case, accents, punctuation and spacing never make two counties. */
function foldCounty(name: string): string {
  return normaliseText(name);
}

function withoutSuffix(folded: string): string {
  for (const suffix of COUNTY_NAME_SUFFIXES) {
    const tail = ` ${suffix.toLowerCase()}`;
    if (folded.endsWith(tail)) return folded.slice(0, -tail.length).trim();
  }
  return folded;
}

/**
 * 🔒 The official county a typed name is (decision 0737): the exact name,
 * folded, or the name with its County, Parish or Borough left off. Two
 * official names answering one typed name answer none, so a county is never
 * guessed; Maryland's Baltimore is its county, because the city's own name
 * keeps its *city*.
 */
export function officialCountyName(typed: string | null | undefined, officialNames: readonly string[]): string | null {
  const folded = foldCounty(typed ?? '');
  if (!folded) return null;
  const exact = officialNames.filter((name) => foldCounty(name) === folded);
  if (exact.length === 1) return exact[0]!;
  if (exact.length > 1) return null;
  const bare = officialNames.filter((name) => withoutSuffix(foldCounty(name)) === withoutSuffix(folded));
  return bare.length === 1 ? bare[0]! : null;
}

/** Two names for one county, the way a territory's County criterion and a location compare (decision 0354, amended by 0737). */
export function sameCounty(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = withoutSuffix(foldCounty(a ?? ''));
  return left !== '' && left === withoutSuffix(foldCounty(b ?? ''));
}

/**
 * The town a building stands in, which a Municipality jurisdiction matches:
 * its municipality where one is recorded, else its mailing city (decision 0406).
 */
export function townOf(location: { municipality?: string | null; locality?: string | null }): string | null {
  const municipality = location.municipality?.trim();
  if (municipality) return municipality;
  return location.locality?.trim() || null;
}

/** A Municipality jurisdiction and a town are one place where the one place-name fold says so: Hopewell and Hopewell Township (decision 0406). */
export function sameTown(a: string | null | undefined, b: string | null | undefined): boolean {
  return samePlaceName(a, b);
}

// ---- a credit note's rows (decision 0738) ----

/** What earlier credits have taken off one invoice line: the quantity, and the tax row by row, keyed as `taxRowKey` keys a row. */
export interface CreditedSharesSoFar {
  readonly quantity: number;
  readonly byRow: Readonly<Record<string, number>>;
}

/**
 * 🔒 A credited line's tax, row by row (decision 0738, amended in review
 * 2026-09-25). Each row its invoice line was charged in gives back its own
 * share for the quantity credited, and no other row gives back anything.
 *
 * 🔨 The rounding is the one a line's whole tax was already shared by
 * (`taxShare`): the rounding, half-up, of the running total credited, less
 * what earlier credits took from that row. The line's tax is the sum of its
 * shares, so the last credit of a line closes each of its rows exactly.
 */
export function creditedLineShares(invoiceShares: readonly TaxLineShare[], creditedQuantity: number, lineQuantity: number, soFar: CreditedSharesSoFar): TaxLineShare[] {
  return invoiceShares.map((share) => ({
    ...share,
    taxCents: taxShare(share.taxCents, creditedQuantity, lineQuantity, { quantity: soFar.quantity, taxCents: soFar.byRow[taxRowKey(share)] ?? 0 }),
  }));
}

/**
 * 🔒 A Credit Note's rows, at its invoice's own jurisdictions, labels and rates
 * (decision 0219's reason; decision 0738). Each carries the shares its credited
 * lines gave back to that row as its tax, and those lines' credited amounts as
 * its base. A row nothing gave anything back to is not written, so the rows add
 * to exactly the note's lines.
 */
export function creditNoteTaxRows(invoiceRows: readonly TaxJurisdictionTotal[], credited: readonly { readonly baseCents: number; readonly shares: readonly TaxLineShare[] }[]): TaxJurisdictionTotal[] {
  return invoiceRows.flatMap((row) => {
    const key = taxRowKey(row);
    let taxableBaseCents = 0;
    let taxCents = 0;
    for (const line of credited) {
      const share = line.shares.find((one) => taxRowKey(one) === key);
      if (!share) continue;
      taxableBaseCents += line.baseCents;
      taxCents += share.taxCents;
    }
    return taxCents === 0 ? [] : [{ ...row, taxableBaseCents, taxCents }];
  });
}

/**
 * A Credit Note's rows against an invoice that stored rows and no shares: one
 * saved before an invoice recorded each line's share (decision 0738). The
 * note's tax is spread over the invoice's rows in proportion to what each
 * charged, largest remainder first, and every row reads the note's whole base.
 *
 * 💰 It is exact where every taxed line of the invoice was charged in every
 * row, which held until a lower rate gave one state two rows (decision 0901).
 * No deployed invoice carries rows without shares, because both arrive in one
 * deploy; a database that ran the rows first can hold some.
 */
export function proportionalCreditNoteTaxRows(invoiceRows: readonly TaxJurisdictionTotal[], creditTaxCents: number, creditBaseCents: number): TaxJurisdictionTotal[] {
  const weights = invoiceRows.map((row) => Math.abs(row.taxCents));
  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  if (invoiceRows.length === 0 || weightSum === 0 || creditTaxCents === 0) return [];
  const sign = creditTaxCents < 0 ? -1 : 1;
  const whole = Math.abs(creditTaxCents);
  const exact = weights.map((w) => (whole * w) / weightSum);
  const shares = exact.map((x) => Math.floor(x));
  let left = whole - shares.reduce((sum, s) => sum + s, 0);
  const byRemainder = exact.map((x, i) => ({ i, r: x - Math.floor(x) })).sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    shares[i]! += 1;
    left -= 1;
  }
  return invoiceRows
    .map((row, i) => ({ ...row, taxableBaseCents: creditBaseCents, taxCents: sign * shares[i]! }))
    .filter((row) => row.taxCents !== 0);
}
